import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useCurrentWorkspace } from './workspace';

// ── Types ───────────────────────────────────────────────────────────

export type ProjectType =
  | 'marketing_campaign' | 'web_design' | 'seo_sem' | 'email_marketing'
  | 'paid_advertising' | 'content_marketing' | 'client_onboarding'
  | 'subscription_renewal' | 'event_marketing' | 'traditional_marketing'
  | 'social_media' | 'custom';

export type ProjectStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
export type TaskPriority = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'IN_REVIEW' | 'BLOCKED' | 'DONE' | 'CANCELLED';

export interface Project {
  publicId: string;
  name: string;
  description: string | null;
  projectType: ProjectType;
  status: ProjectStatus;
  priority: TaskPriority;
  color: string | null;
  ownerPublicId: string;
  ownerName: string;
  startDate: string | null;
  targetDate: string | null;
  completedAt: string | null;
  budgetCents: number | null;
  memberCount: number;
  taskCount: number;
  completedTaskCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectStage {
  publicId: string;
  name: string;
  color: string | null;
  sortOrder: number;
  isTerminal: boolean;
  taskCount: number;
}

export interface ProjectMember {
  userPublicId: string;
  userName: string;
  userEmail: string;
  avatarUrl: string | null;
  projectRole: 'LEAD' | 'MEMBER' | 'VIEWER';
  joinedAt: string;
}

export interface Task {
  publicId: string;
  projectPublicId: string | null;
  stagePublicId: string | null;
  parentTaskPublicId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assigneePublicId: string | null;
  assigneeName: string | null;
  assignees: Array<{ publicId: string; name: string; avatarUrl: string | null }>;
  reporterPublicId: string;
  reporterName: string;
  dueDate: string | null;
  dueTime: string | null;
  startDate: string | null;
  estimatedMinutes: number | null;
  actualMinutes: number | null;
  sortOrder: number;
  completedAt: string | null;
  labels: Array<{ publicId: string; name: string; color: string }>;
  subtaskCount: number;
  completedSubtaskCount: number;
  syncToCalendar: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TaskActivity {
  publicId: string;
  userPublicId: string | null;
  userName: string | null;
  userAvatarUrl: string | null;
  activityType: string;
  body: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface WorkflowTemplate {
  publicId: string;
  name: string;
  description: string | null;
  department: string;
  projectType: string;
  isSystem: boolean;
  icon: string | null;
  color: string | null;
  usageCount: number;
  stageCount: number;
  taskCount: number;
  createdAt: string;
}

export interface WorkflowTemplateDetail extends WorkflowTemplate {
  stages: Array<{ name: string; color: string | null; sortOrder: number; isTerminal: boolean }>;
  tasks: Array<{ title: string; description: string | null; stageIndex: number; priority: string; estimatedMinutes: number | null; labels: string[] }>;
}

// ── Project hooks ───────────────────────────────────────────────────

export function useProjects(filters?: { status?: ProjectStatus; projectType?: ProjectType }) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['projects', ws?.publicId, filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.projectType) params.set('projectType', filters.projectType);
      const qs = params.toString();
      return api<Project[]>(`/workspaces/${ws!.publicId}/projects${qs ? `?${qs}` : ''}`);
    },
    enabled: !!ws,
  });
}

export function useProject(projectId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['projects', ws?.publicId, projectId],
    queryFn: () => api<Project>(`/workspaces/${ws!.publicId}/projects/${projectId}`),
    enabled: !!ws && !!projectId,
  });
}

export function useCreateProject() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      projectType?: ProjectType;
      priority?: TaskPriority;
      color?: string;
      startDate?: string;
      targetDate?: string;
      templatePublicId?: string;
      memberPublicIds?: string[];
    }) =>
      api<{ publicId: string }>(`/workspaces/${ws!.publicId}/projects`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, ...input }: {
      projectId: string;
      name?: string;
      description?: string;
      status?: ProjectStatus;
      priority?: TaskPriority;
      color?: string | null;
      startDate?: string | null;
      targetDate?: string | null;
    }) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api<{ status: 'deleted' | 'requested' }>(`/workspaces/${ws!.publicId}/projects/${projectId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-trash'] });
    },
  });
}

export interface TrashProject {
  publicId: string;
  name: string;
  deletedAt: string;
  deletedByName: string | null;
  daysLeft: number;
}

export interface DeletionRequest {
  id: string;
  itemType: 'project' | 'task';
  itemPublicId: string;
  itemTitle: string;
  requestedByName: string;
  createdAt: string;
}

/** Soft-deleted projects (Trash). Admin/owner/super-admin only (403 otherwise). */
export function useProjectTrash(enabled = true) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['project-trash', ws?.publicId],
    queryFn: () =>
      api<{ data: TrashProject[] }>(`/workspaces/${ws!.publicId}/projects/trash`).then((r) => r.data),
    enabled: !!ws && enabled,
  });
}

/** Pending deletion requests. Admin/owner/super-admin only. */
export function useDeletionRequests(enabled = true) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['deletion-requests', ws?.publicId],
    queryFn: () =>
      api<{ data: DeletionRequest[] }>(`/workspaces/${ws!.publicId}/projects/deletion-requests`).then(
        (r) => r.data,
      ),
    enabled: !!ws && enabled,
  });
}

export function useRestoreProject() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-trash'] });
    },
  });
}

export function useReviewDeletion() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, approve }: { requestId: string; approve: boolean }) =>
      api(`/workspaces/${ws!.publicId}/projects/deletion-requests/${requestId}/review`, {
        method: 'POST',
        body: { approve },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deletion-requests'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-trash'] });
    },
  });
}

// ── Stage hooks ─────────────────────────────────────────────────────

export function useProjectStages(projectId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['project-stages', ws?.publicId, projectId],
    queryFn: () => api<ProjectStage[]>(`/workspaces/${ws!.publicId}/projects/${projectId}/stages`),
    enabled: !!ws && !!projectId,
  });
}

export function useCreateStage() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, ...input }: { projectId: string; name: string; color?: string; sortOrder?: number; isTerminal?: boolean }) =>
      api<{ publicId: string }>(`/workspaces/${ws!.publicId}/projects/${projectId}/stages`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-stages'] });
    },
  });
}

export function useUpdateStage() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, stageId, ...input }: {
      projectId: string;
      stageId: string;
      name?: string;
      color?: string;
      sortOrder?: number;
      isTerminal?: boolean;
    }) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}/stages/${stageId}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-stages'] });
    },
  });
}

export function useDeleteStage() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, stageId }: { projectId: string; stageId: string }) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}/stages/${stageId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-stages'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ── Member hooks ────────────────────────────────────────────────────

export function useProjectMembers(projectId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['project-members', ws?.publicId, projectId],
    queryFn: () => api<ProjectMember[]>(`/workspaces/${ws!.publicId}/projects/${projectId}/members`),
    enabled: !!ws && !!projectId,
  });
}

export function useAddProjectMember() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, userPublicId, role }: {
      projectId: string;
      userPublicId: string;
      role?: 'LEAD' | 'MEMBER' | 'VIEWER';
    }) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}/members`, {
        method: 'POST',
        body: { userPublicId, role },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-members'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useRemoveProjectMember() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, userPublicId }: { projectId: string; userPublicId: string }) =>
      api(`/workspaces/${ws!.publicId}/projects/${projectId}/members/${userPublicId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-members'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ── Task hooks ──────────────────────────────────────────────────────

export function useTasks(filters?: {
  projectPublicId?: string;
  stagePublicId?: string;
  status?: TaskStatus;
  assignee?: string;
  priority?: TaskPriority;
  dueBefore?: string;
  dueAfter?: string;
  parent?: string;
}) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['tasks', ws?.publicId, filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.projectPublicId) params.set('project', filters.projectPublicId);
      if (filters?.stagePublicId) params.set('stage', filters.stagePublicId);
      if (filters?.status) params.set('status', filters.status);
      if (filters?.assignee) params.set('assignee', filters.assignee);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.dueBefore) params.set('dueBefore', filters.dueBefore);
      if (filters?.dueAfter) params.set('dueAfter', filters.dueAfter);
      if (filters?.parent) params.set('parent', filters.parent);
      const qs = params.toString();
      return api<Task[]>(`/workspaces/${ws!.publicId}/tasks${qs ? `?${qs}` : ''}`);
    },
    enabled: !!ws,
  });
}

export function useTask(taskId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['tasks', ws?.publicId, 'detail', taskId],
    queryFn: () => api<Task>(`/workspaces/${ws!.publicId}/tasks/${taskId}`),
    enabled: !!ws && !!taskId,
  });
}

export function useCreateTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string;
      projectPublicId?: string;
      stagePublicId?: string;
      parentTaskPublicId?: string;
      priority?: TaskPriority;
      assigneePublicId?: string;
      assigneePublicIds?: string[];
      dueDate?: string;
      dueTime?: string;
      startDate?: string;
      estimatedMinutes?: number;
      labelPublicIds?: string[];
      syncToCalendar?: boolean;
    }) =>
      api<{ publicId: string }>(`/workspaces/${ws!.publicId}/tasks`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...input }: {
      taskId: string;
      title?: string;
      description?: string;
      status?: TaskStatus;
      priority?: TaskPriority;
      assigneePublicId?: string | null;
      assigneePublicIds?: string[];
      stagePublicId?: string | null;
      dueDate?: string | null;
      dueTime?: string | null;
      startDate?: string | null;
      estimatedMinutes?: number | null;
      actualMinutes?: number | null;
      labelPublicIds?: string[];
      syncToCalendar?: boolean;
    }) =>
      api(`/workspaces/${ws!.publicId}/tasks/${taskId}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

export interface MyTask {
  publicId: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  projectPublicId: string | null;
  projectName: string | null;
  verifiedAt: string | null;
}

/** All tasks assigned to the current user across the workspace (My Tasks summary). */
export function useMyTasks() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['my-tasks', ws?.publicId],
    queryFn: () =>
      api<{ data: MyTask[] }>(`/workspaces/${ws!.publicId}/tasks/mine`).then((r) => r.data),
    enabled: !!ws,
    staleTime: 15_000,
  });
}

/** Second-stage verification (final tick) — verified=true scratches it off. */
export function useVerifyTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, verified }: { taskId: string; verified: boolean }) =>
      api(`/workspaces/${ws!.publicId}/tasks/${taskId}/verify`, {
        method: 'POST',
        body: { verified },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export interface BriefTask {
  publicId: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  projectName: string | null;
  workspaceName: string;
  workspacePublicId: string;
  verifiedAt: string | null;
}

/** Every task assigned to me across ALL workspaces — powers Domina's brief. */
export function useAllMyTasks() {
  return useQuery({
    queryKey: ['all-my-tasks'],
    queryFn: () => api<{ data: BriefTask[] }>(`/me/tasks`).then((r) => r.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** Toggle a task's done state in ANY workspace (used by the cross-workspace brief). */
export function useToggleTaskDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspacePublicId,
      taskId,
      status,
    }: {
      workspacePublicId: string;
      taskId: string;
      status: TaskStatus;
    }) =>
      api(`/workspaces/${workspacePublicId}/tasks/${taskId}`, {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-my-tasks'] });
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDeleteTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api<{ status: 'deleted' | 'requested' }>(`/workspaces/${ws!.publicId}/tasks/${taskId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
      qc.invalidateQueries({ queryKey: ['all-my-tasks'] });
      qc.invalidateQueries({ queryKey: ['task-trash'] });
    },
  });
}

export interface TrashTask {
  publicId: string;
  title: string;
  projectName: string | null;
  deletedAt: string;
  deletedByName: string | null;
  daysLeft: number;
}

/** Soft-deleted tasks (Trash). Admin/owner/super-admin only. */
export function useTaskTrash(enabled = true) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['task-trash', ws?.publicId],
    queryFn: () =>
      api<{ data: TrashTask[] }>(`/workspaces/${ws!.publicId}/tasks/trash`).then((r) => r.data),
    enabled: !!ws && enabled,
  });
}

export function useRestoreTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      api(`/workspaces/${ws!.publicId}/tasks/${taskId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['task-trash'] });
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
    },
  });
}

export function useReorderTask() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...input }: { taskId: string; stagePublicId?: string | null; sortOrder: number }) =>
      api(`/workspaces/${ws!.publicId}/tasks/${taskId}/reorder`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// ── Activity hooks ──────────────────────────────────────────────────

export function useTaskActivities(taskId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['task-activities', ws?.publicId, taskId],
    queryFn: () => api<TaskActivity[]>(`/workspaces/${ws!.publicId}/tasks/${taskId}/activities`),
    enabled: !!ws && !!taskId,
  });
}

export function useCreateTaskActivity() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: string }) =>
      api<{ publicId: string }>(`/workspaces/${ws!.publicId}/tasks/${taskId}/activities`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: () => {
      // Prefix match — the full key is ['task-activities', wsPublicId, taskId],
      // so keying on undefined never matched and the comment never appeared.
      qc.invalidateQueries({ queryKey: ['task-activities'] });
    },
  });
}

// ── Template hooks ──────────────────────────────────────────────────

export function useSystemTemplates() {
  return useQuery({
    queryKey: ['templates', 'system'],
    queryFn: () => api<WorkflowTemplate[]>('/templates/system'),
  });
}

export function useWorkspaceTemplates() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['templates', ws?.publicId],
    queryFn: () => api<WorkflowTemplate[]>(`/workspaces/${ws!.publicId}/templates`),
    enabled: !!ws,
  });
}

export function useTemplateDetail(templateId: string | undefined) {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['templates', 'detail', templateId],
    queryFn: () => {
      // System templates use the system route
      return api<WorkflowTemplateDetail>(`/templates/system/${templateId}`);
    },
    enabled: !!templateId,
  });
}

// ── Label hooks ─────────────────────────────────────────────────────

export function useTaskLabels() {
  const ws = useCurrentWorkspace();
  return useQuery({
    queryKey: ['task-labels', ws?.publicId],
    queryFn: () => api<Array<{ publicId: string; name: string; color: string }>>(`/workspaces/${ws!.publicId}/tasks/labels`),
    enabled: !!ws,
  });
}

export function useCreateTaskLabel() {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string }) =>
      api<{ publicId: string }>(`/workspaces/${ws!.publicId}/tasks/labels`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['task-labels'] });
    },
  });
}
